import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, KeyRound } from 'lucide-react';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';

const PartnerRegister: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isLoading } = usePartnerAuth();
  const inviteToken = searchParams.get('token') || '';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation state
  interface PasswordRules {
    minLength: boolean;
    hasUpperCase: boolean;
    hasLowerCase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  }

  const [passwordRules, setPasswordRules] = useState<PasswordRules>({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  const [passwordsMatch, setPasswordsMatch] = useState(false);

  // Validate password rules
  useEffect(() => {
    const password = formData.password;
    setPasswordRules({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });

    // Check if passwords match
    if (formData.confirmPassword) {
      setPasswordsMatch(formData.password === formData.confirmPassword);
    } else {
      setPasswordsMatch(false);
    }
  }, [formData.password, formData.confirmPassword]);

  // Check if all password rules are met
  const allPasswordRulesMet = Object.values(passwordRules).every(rule => rule);
  const isFormValid =
    formData.email &&
    inviteToken &&
    allPasswordRulesMet &&
    passwordsMatch;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!inviteToken) {
      setError('No invite token provided. Please use the link from your invitation email.');
      return;
    }

    // Form validation is already done via isFormValid
    if (!isFormValid) {
      setError('Please complete all required fields and meet password requirements');
      return;
    }

    try {
      await register(
        formData.email,
        formData.password,
        formData.displayName || undefined,
        inviteToken
      );

      setSuccess(true);
      // Redirect to dashboard after 2 seconds (email auto-verified)
      setTimeout(() => {
        navigate('/partner/dashboard');
      }, 2000);
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  // Styles (same as PartnerLogin for consistency)
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px'
  };

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    width: '100%',
    maxWidth: '500px'
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '30px'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a202c',
    marginBottom: '8px'
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#718096'
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  };

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748'
  };

  const inputStyle: React.CSSProperties = {
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s',
    outline: 'none'
  };

  const submitButtonStyle: React.CSSProperties = {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.2s',
    marginTop: '10px',
    width: '100%'
  };

  const errorBoxStyle: React.CSSProperties = {
    padding: '12px 16px',
    backgroundColor: '#fed7d7',
    border: '1px solid #fc8181',
    borderRadius: '8px',
    color: '#c53030',
    fontSize: '14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const successBoxStyle: React.CSSProperties = {
    padding: '12px 16px',
    backgroundColor: '#c6f6d5',
    border: '1px solid #68d391',
    borderRadius: '8px',
    color: '#22543d',
    fontSize: '14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const footerStyle: React.CSSProperties = {
    marginTop: '24px',
    textAlign: 'center'
  };

  const footerTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#718096'
  };

  const linkStyle: React.CSSProperties = {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '500'
  };

  if (!inviteToken) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={cardStyle}>
          <div style={errorBoxStyle}>
            <span><AlertTriangle size={16} /></span>
            <div>
              <strong>No invitation token found</strong>
              <p style={{ margin: '4px 0 0 0' }}>
                Registration requires an invitation link. Please contact your administrator.
              </p>
            </div>
          </div>
          <div style={footerStyle}>
            <p style={footerTextStyle}>
              Already have an account?{' '}
              <Link to="/partner/login" style={linkStyle}>
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={cardStyle}>
          <div style={successBoxStyle}>
            <span><CheckCircle size={16} /></span>
            <div>
              <strong>Registration successful!</strong>
              <p style={{ margin: '4px 0 0 0' }}>
                Your account has been created and you are now logged in. Redirecting to dashboard...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Partner Registration</h1>
          <p style={subtitleStyle}>Create your partner account</p>
        </div>

        {error && (
          <div style={errorBoxStyle}>
            <span><AlertTriangle size={16} /></span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={{ padding: '12px 16px', backgroundColor: '#ebf8ff', border: '1px solid #90cdf4', borderRadius: '8px', fontSize: '14px', color: '#2b6cb0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><KeyRound size={16} /></span>
            <span>You are registering with an invitation token.</span>
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="email" style={labelStyle}>
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="partner@example.com"
              style={inputStyle}
              disabled={isLoading}
              required
            />
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="displayName" style={labelStyle}>
              Display Name (Optional)
            </label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="John Doe"
              style={inputStyle}
              disabled={isLoading}
            />
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="password" style={labelStyle}>
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              style={inputStyle}
              disabled={isLoading}
              required
            />

            {/* Password Requirements */}
            {formData.password && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>
                  Password Requirements:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: passwordRules.minLength ? '#48bb78' : '#cbd5e0' }}>
                      {passwordRules.minLength ? '✓' : '○'}
                    </span>
                    <span style={{ color: passwordRules.minLength ? '#2d3748' : '#718096' }}>
                      At least 8 characters
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: passwordRules.hasUpperCase ? '#48bb78' : '#cbd5e0' }}>
                      {passwordRules.hasUpperCase ? '✓' : '○'}
                    </span>
                    <span style={{ color: passwordRules.hasUpperCase ? '#2d3748' : '#718096' }}>
                      One uppercase letter (A-Z)
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: passwordRules.hasLowerCase ? '#48bb78' : '#cbd5e0' }}>
                      {passwordRules.hasLowerCase ? '✓' : '○'}
                    </span>
                    <span style={{ color: passwordRules.hasLowerCase ? '#2d3748' : '#718096' }}>
                      One lowercase letter (a-z)
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: passwordRules.hasNumber ? '#48bb78' : '#cbd5e0' }}>
                      {passwordRules.hasNumber ? '✓' : '○'}
                    </span>
                    <span style={{ color: passwordRules.hasNumber ? '#2d3748' : '#718096' }}>
                      One number (0-9)
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                    <span style={{ color: passwordRules.hasSpecialChar ? '#48bb78' : '#cbd5e0' }}>
                      {passwordRules.hasSpecialChar ? '✓' : '○'}
                    </span>
                    <span style={{ color: passwordRules.hasSpecialChar ? '#2d3748' : '#718096' }}>
                      One special character (!@#$%^&*...)
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="confirmPassword" style={labelStyle}>
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="••••••••"
              style={inputStyle}
              disabled={isLoading}
              required
            />

            {/* Password Match Indicator */}
            {formData.confirmPassword && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: passwordsMatch ? '#48bb78' : '#f56565' }}>
                  {passwordsMatch ? '✓' : '✗'}
                </span>
                <span style={{ color: passwordsMatch ? '#2d3748' : '#718096' }}>
                  {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </span>
              </div>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...submitButtonStyle,
              opacity: (isLoading || !isFormValid) ? 0.5 : 1,
              cursor: (isLoading || !isFormValid) ? 'not-allowed' : 'pointer'
            }}
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>

          {!isFormValid && formData.password && (
            <p style={{ fontSize: '13px', color: '#e53e3e', marginTop: '8px', textAlign: 'center' }}>
              Please complete all requirements above to enable registration
            </p>
          )}
        </form>

        <div style={footerStyle}>
          <p style={footerTextStyle}>
            Already have an account?{' '}
            <Link to="/partner/login" style={linkStyle}>
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegister;

